/**
 * The review API client (AB#955377). One class over the shared
 * `@xplatform/platform` `HttpClient`, which throws the single `ApiError` on
 * every failure — callers branch on `status` / `errorCode` (403 /
 * 409 image_already_decided | review_pending | run_not_generated / 422),
 * never on message text, and use `isApiError` (survives module federation).
 *
 * The client is injected a `HttpClient` so tests can pass a fake; the default
 * export is built from `VITE_API_BASE_URL`.
 */

import { HttpClient } from "@xplatform/platform";
import type {
  Page,
  PublishResponse,
  RegenerateRequest,
  RegenerateResponse,
  ReviewDecisionRequest,
  ReviewDecisionResponse,
  ReviewImageDetail,
  ReviewImageListResponse,
  ReviewRunSummary,
} from "@models/review";

/** The subset of `HttpClient` the review client depends on. A fake in tests
 * only needs to implement this. */
export interface ReviewHttpClient {
  makeAuthenticatedRequest<T>(
    endpoint: string,
    options?: RequestInit & { baseUrl?: string },
  ): Promise<T>;
}

const enc = encodeURIComponent;

export class ReviewApi {
  private readonly http: ReviewHttpClient;

  constructor(http: ReviewHttpClient) {
    this.http = http;
  }

  /** `GET /review/runs` — runs in GENERATED (awaiting review), newest first. */
  async listGeneratedRuns(params?: {
    cursor?: string;
    limit?: number;
  }): Promise<Page<ReviewRunSummary>> {
    const query = new URLSearchParams();
    if (params?.cursor) query.set("cursor", params.cursor);
    if (params?.limit != null) query.set("limit", String(params.limit));
    const qs = query.toString();
    return this.http.makeAuthenticatedRequest<Page<ReviewRunSummary>>(
      `/review/runs${qs ? `?${qs}` : ""}`,
    );
  }

  /** `GET /review/runs/{runId}/images` — per-image state and review counts. */
  async listImages(runId: string): Promise<ReviewImageListResponse> {
    return this.http.makeAuthenticatedRequest<ReviewImageListResponse>(
      `/review/runs/${enc(runId)}/images`,
    );
  }

  /** `GET /review/runs/{runId}/images/{requestId}/{objId}` — image detail. */
  async getImage(
    runId: string,
    requestId: string,
    objId: string,
  ): Promise<ReviewImageDetail> {
    return this.http.makeAuthenticatedRequest<ReviewImageDetail>(
      `/review/runs/${enc(runId)}/images/${enc(requestId)}/${enc(objId)}`,
    );
  }

  /** `POST .../decision` — approve or reject one image. */
  async decide(
    runId: string,
    requestId: string,
    objId: string,
    body: ReviewDecisionRequest,
  ): Promise<ReviewDecisionResponse> {
    return this.http.makeAuthenticatedRequest<ReviewDecisionResponse>(
      `/review/runs/${enc(runId)}/images/${enc(requestId)}/${enc(objId)}/decision`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  /** `POST /review/runs/{runId}/publish` — send the return message. */
  async publish(runId: string): Promise<PublishResponse> {
    return this.http.makeAuthenticatedRequest<PublishResponse>(
      `/review/runs/${enc(runId)}/publish`,
      { method: "POST" },
    );
  }

  /** `POST /review/runs/{runId}/regenerate` — send the regenerate message for
   * the listed FAILED / generation_failed images (generation.admin only). */
  async regenerate(
    runId: string,
    body: RegenerateRequest,
  ): Promise<RegenerateResponse> {
    return this.http.makeAuthenticatedRequest<RegenerateResponse>(
      `/review/runs/${enc(runId)}/regenerate`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }
}

/** The app-wide client, pointed at the configured generation_api base URL. */
export const createReviewApi = (
  getAccessToken?: () => Promise<string | null | undefined>,
): ReviewApi => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "";
  return new ReviewApi(new HttpClient(baseUrl, getAccessToken));
};
